import { debounce } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { KTSVG } from '../../../../../../_metronic/helpers'
import { PageTitle } from '../../../../../../_metronic/layout/core'
import { LoadingComponent } from '../../../../../../components/loadingComponent/LoadingComponent'
import NoRecordFound from '../../../../../../components/noRecord/NoRecordFound'
import Pagination from '../../../../../../components/pagination/Pagination'
import RefreshComponent from '../../../../../../components/refreshComponent/RefreshComponent'
import TableHeading from '../../../../../../components/tableHeading/TableHeading'
import {
  useGetAllOrgSettingGroupQuery,
  useAddOrgSettingGroupMutation,
  useUpdateOrgSettingGroupMutation,
  useChangeOrgSettingGroupStatusMutation,
} from '../../../../../../services/OrgSettingsApi'
import OrgSettingGroupModal from './OrgSettingGroupModal'
import ReactReadMoreReadLess from "react-read-more-read-less";
import DateComp from '../../../../../../components/dateComponent/DateComp'
import Swal from 'sweetalert2';

interface TableColumn {
  id: string
  label: string
  type?: string
  className?: string
  labelClassName?: string
  sortable?: boolean
}

const tableColumns: TableColumn[] = [
  { id: 'groupName', label: 'Group Name', className: 'ps-4', sortable: true },
  { id: 'description', label: 'Description', className: 'ps-4', sortable: true, type: 'readmore' },
  { id: 'isActive', label: 'Status', className: 'ps-4 text-center', type: 'boolean', sortable: true },
  { id: 'createdOn', label: 'Created Date', type: 'date', className: 'w-100px text-center', labelClassName: 'text-center', sortable: true },
  { id: 'createdBy', label: 'Created By', className: 'ps-4', sortable: true },
  { id: 'modifiedOn', label: 'Modified Date', type: 'date', className: 'w-100px text-center', labelClassName: 'text-center', sortable: true },
  { id: 'modifiedBy', label: 'Modified By', className: 'ps-4', sortable: true },
]

const OrgSettingGroup = () => {
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [searchStr, setSearchStr] = useState('')
  const [immediateSearchStr, setImmediateSearchStr] = useState('')
  const [sortBy, setSortBy] = useState('groupName')
  const [sortOrder, setSortOrder] = useState('ASC')
  const [sortedColumn, setSortedColumn] = useState('groupName')
  const [isLoading, setIsLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editingItem, setEditingItem] = useState<any>(null)
  const [loadingStatusId, setLoadingStatusId] = useState<number | null>(null)
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())

  const { data, isFetching, refetch, isSuccess } = useGetAllOrgSettingGroupQuery({
    pageSize,
    pageNumber,
    sortBy,
    sortOrder,
    searchStr,
    filterByStatus: '',
  })

  const [addOrgSettingGroup] = useAddOrgSettingGroupMutation()
  const [updateOrgSettingGroup] = useUpdateOrgSettingGroupMutation()
  const [changeOrgSettingGroupStatus] = useChangeOrgSettingGroupStatusMutation()

  const handlePageSizeChange = (e: any) => {
    setPageSize(parseInt(e.target.value))
    setPageNumber(1)
  }

  const handlePageClick = (e: any) => {
    setPageNumber(e.selected + 1)
  }

  const handleSort = (property: string) => {
    setSortBy(property)
    if (sortedColumn === property) {
      setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC')
    } else {
      setSortedColumn(property)
      setSortOrder('ASC')
    }
  }

  const handleSearchChange = (e: any) => {
    const { value } = e.target
    setImmediateSearchStr(value)
    delayedSearch(value)
    setPageNumber(1)
  }

  const delayedSearch = useCallback(
    debounce((searchValue: string) => {
      setSearchStr(searchValue)
    }, 500),
    []
  )

  const handleRefresh = () => {
    refetch().then(() => {
      if (isSuccess) toast.success('Data refreshed successfully')
    })
  }

  useEffect(() => {
    setIsLoading(isFetching)
  }, [isFetching])

  const handleAddNew = () => {
    setEditingItem(null)
    setShowModal(true)
  }

  const handleEdit = (item: any) => {
    setEditingItem(item)
    setShowModal(true)
  }

  const handleStatusToggle = async (item: any) => {
    const newStatus = !item.isActive
    const statusText = newStatus ? 'activate' : 'deactivate'
    const actionText = newStatus ? 'Activate' : 'Deactivate'

    const result = await Swal.fire({
      title: `Are you sure?`,
      text: `Do you want to ${statusText} "${item.groupName}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: newStatus ? '#28a745' : '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: `Yes, ${actionText} it!`,
      cancelButtonText: 'Cancel'
    })

    if (result.isConfirmed) {
      setLoadingStatusId(item.orgSettingGroupId)
      try {
        const payload = {
          orgSettingGroupId: item.orgSettingGroupId,
          status: newStatus
        }

        await changeOrgSettingGroupStatus(payload).unwrap()
        toast.success(`Organization Setting Group ${statusText}d successfully`)
        await refetch()
      } catch (error: any) {
        console.error('Status change failed:', error)
        toast.error(error?.data?.message || `Failed to ${statusText} the organization setting group`)
      } finally {
        setLoadingStatusId(null)
      }
    }
  }

  const handleModalClose = () => {
    setShowModal(false)
    setEditingItem(null)
  }

  const handleModalSave = async (payload: any) => {
    try {
      if (editingItem) {
        await updateOrgSettingGroup(payload).unwrap()
        toast.success('Organization Setting Group updated successfully')
      } else {
        await addOrgSettingGroup(payload).unwrap()
        toast.success('Organization Setting Group added successfully')
      }
      handleModalClose()
      refetch()
    } catch (error: any) {
      console.error('Save failed:', error)
      toast.error(error?.data?.message || 'An error occurred')
    }
  }

  const toggleRowExpansion = (orgSettingGroupId: number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev)
      if (newSet.has(orgSettingGroupId)) {
        newSet.delete(orgSettingGroupId)
      } else {
        newSet.add(orgSettingGroupId)
      }
      return newSet
    })
  }

  const tableData = data?.result?.data || []
  const totalRecords = data?.result?.totalRecords || 0

  const renderCellContent = (item: any, column: TableColumn) => {
    const value = item[column.id]
    if (!value && value !== 0 && value !== false) return '-'
    if (column.type === 'boolean') {
      return (
        <div className="d-flex align-items-center justify-content-center gap-3">
          {value ?
            <span className='badge badge-light-success'>Active</span> :
            <span className='badge badge-light-danger'>Inactive</span>
          }
          <div className="form-check form-switch">
            <input
              className="form-check-input cursor-pointer"
              type="checkbox"
              role="switch"
              id={`flexSwitchCheckChecked-${item.orgSettingGroupId}`}
              checked={item.isActive}
              onChange={() => handleStatusToggle(item)}
              disabled={loadingStatusId === item.orgSettingGroupId}
            />
            <label className="form-check-label" htmlFor={`flexSwitchCheckChecked-${item.orgSettingGroupId}`}></label>
          </div>
        </div>
      )
    }
    if (column.type === 'date') {
      return <DateComp formattedDate={value} />
    }
    if (column.type === 'readmore') {
      return value && <ReactReadMoreReadLess
        charLimit={100}
        readMoreText={"Read more"}
        readLessText={"Read less"}
        readMoreClassName="readMore"
        readLessClassName="readLess"
      >
        {value}
      </ReactReadMoreReadLess>
    }
    return value
  }

  const renderCell = (item: any, column: TableColumn) => {
    if (column.type === 'boolean') {
      return <td className={column.className || 'ps-4 text-center'}>{renderCellContent(item, column)}</td>
    }
    if (column.type === 'readmore') {
      return <td style={{ whiteSpace: 'normal', wordWrap: 'break-word', maxWidth: '250px' }}>{renderCellContent(item, column)}</td>
    }
    return <td className={column.className || 'ps-4 text-center'}>{renderCellContent(item, column)}</td>
  }

  const renderSettingsDetails = (item: any): JSX.Element[] => {
    const settings = item.settings || []

    if (settings.length === 0) {
      return [
        <tr key={`no-settings-${item.orgSettingGroupId}`}>
          <td colSpan={tableColumns.length + 1} className="text-center py-4">
            <div className="text-muted">No settings available for this group</div>
          </td>
        </tr>
      ]
    }

    return settings.map((setting: any, idx: number) => (
      <tr key={`setting-${item.orgSettingGroupId}-${idx}`} className="bg-light">
        <td className="ps-4" style={{ backgroundColor: '#f8f9fa' }}>
          <div className="fw-bold">Setting #{idx + 1}</div>
        </td>
        <td colSpan={tableColumns.length + 1} className="p-0">
          <div className="p-3">
            <div className="row mb-2">
              <div className="col-md-3 fw-bold">Setting Key:</div>
              <div className="col-md-9">{setting.settingKey || '-'}</div>
            </div>
            <div className="row mb-2">
              <div className="col-md-3 fw-bold">Setting Value:</div>
              <div className="col-md-9 text-wrap"> 
                <ReactReadMoreReadLess
                charLimit={100}
                readMoreText={"Read more"}
                readLessText={"Read less"}
                readMoreClassName="readMore"
                readLessClassName="readLess"
              >
                {setting.settingValue || '-'}
              </ReactReadMoreReadLess></div>
            </div>
            <div className="row mb-2">
              <div className="col-md-3 fw-bold">Setting Detail:</div>
              <div className="col-md-9 text-wrap">
                <ReactReadMoreReadLess
                  charLimit={100}
                  readMoreText={"Read more"}
                  readLessText={"Read less"}
                  readMoreClassName="readMore"
                  readLessClassName="readLess"
                >
                  {setting.settingDetail || '-'}
                </ReactReadMoreReadLess>
              </div>
            </div>
            <div className="row">
              <div className="col-md-3 fw-bold">Status:</div>
              <div className="col-md-9">
                {setting.isActive ?
                  <span className='badge badge-light-success'>Active</span> :
                  <span className='badge badge-light-danger'>Inactive</span>
                }
              </div>
            </div>
          </div>
        </td>
      </tr>
    ))
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>Organization Setting Groups</PageTitle>

      {/* Define all tooltips */}
      <Tooltip id="edit-tooltip" place="top" />
      <Tooltip id="refresh-tooltip" place="top" />
      <Tooltip id="details-tooltip" place="top" />

      <div className='card mb-5'>
        <div className='card-body pb-8 pt-5'>
          <div className='row mb-5'>
            <div className='col-6'>
              <div className='d-flex align-items-center gap-3'>
                <label className='form-label fs-6 fw-bold mb-0'>Search:</label>
                <input
                  type='text'
                  className='form-control form-control-sm w-250px'
                  placeholder='Search by group name or description...'
                  value={immediateSearchStr}
                  onChange={handleSearchChange}
                />
              </div>
            </div>
            <div className='col-6'>
              <div className='d-flex align-items-center justify-content-end gap-2'>
                <div data-tooltip-id="refresh-tooltip" data-tooltip-content="Refresh Data">
                  <RefreshComponent onClick={handleRefresh} />
                </div>
                <button type='button' className='btn btn-sm btn-primary' onClick={handleAddNew}>
                  <i className='fa fa-plus fs-4'></i>
                  Add New
                </button>
              </div>
            </div>
          </div>
          <div className='col-md-12'>
            <div className='table-responsive table-height'>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                <thead>
                  <tr className='fw-bold text-muted'>
                    {tableColumns.map(column => (
                      <TableHeading
                        key={column.id}
                        label={column.label}
                        sortedColumn={sortedColumn}
                        sortOrder={sortOrder}
                        handleSort={handleSort}
                        columnId={column.id}
                        className={column.className}
                        labelClassName={column.labelClassName}
                      />
                    ))}
                    <th className='text-end rounded-end pe-2'>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!isLoading && (
                    <>
                      {tableData.length > 0 ? (
                        tableData.flatMap((item: any, index: number) => {
                          const isExpanded = expandedRows.has(item.orgSettingGroupId)
                          const rows: JSX.Element[] = [
                            // Main row
                            <tr key={`main-${item.orgSettingGroupId}`}>
                              {tableColumns.map(column => renderCell(item, column))}
                              <td className='text-end pe-2'>
                                <div className='d-flex justify-content-end gap-2'>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-icon btn-info'
                                    onClick={() => toggleRowExpansion(item.orgSettingGroupId)}
                                    data-tooltip-id="details-tooltip"
                                    data-tooltip-content={isExpanded ? 'Hide Details' : 'Show Details'}
                                  >
                                    <i className={`fa ${isExpanded ? 'fa-eye-slash' : 'fa-eye'} fs-4`}></i>
                                  </button>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-icon btn-primary'
                                    onClick={() => handleEdit(item)}
                                    data-tooltip-id="edit-tooltip"
                                    data-tooltip-content="Edit"
                                  >
                                    <i className='fa fa-pencil fs-4'></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ]

                          // Add settings detail rows if expanded
                          if (isExpanded) {
                            rows.push(...renderSettingsDetails(item))
                          }

                          return rows
                        })
                      ) : (
                        <tr>
                          <td colSpan={tableColumns.length + 1}>
                            <NoRecordFound />
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                  {isLoading && (
                    <tr>
                      <td colSpan={tableColumns.length + 1} className='text-center'>
                        <LoadingComponent />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              totalResult={totalRecords === 0 ? 0 : (pageNumber - 1) * pageSize + 1}
              toResult={totalRecords === 0 ? 0 : Math.min(pageNumber * pageSize, totalRecords)}
              ofResult={totalRecords}
              onChange={handlePageSizeChange}
              pageSize={pageSize}
              onPageChange={handlePageClick}
              pageCount={data?.result?.totalPages || 0}
              currentPage={pageNumber}
            />
          </div>
        </div>
      </div>
      <OrgSettingGroupModal
        show={showModal}
        onClose={handleModalClose}
        onSave={handleModalSave}
        editingItem={editingItem}
      />
    </>
  )
}

export default OrgSettingGroup